// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { customTokens } from '../../styles';
import { Alert } from '@fluentui/react-components/unstable';
import { Dismiss16Regular } from '@fluentui/react-icons';
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { removeAlert } from '../../redux/features/app/appSlice';
import { EffectivePromptsDialog } from '../chat/prompt-dialog/EffectivePromptsDialog';
import { Alert as TypeAlert } from '../../redux/features/app/AppState';
import { AlertDialogs } from '../../libs/models/AlertDialogs';

const useClasses = makeStyles({
    alert: {
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground1,
        backgroundColor: tokens.colorNeutralBackground6,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        ...shorthands.borderRadius(customTokens.borderRadiusNone),
        borderTopLeftRadius: customTokens.borderRadiusMedium,
        borderTopRightRadius: customTokens.borderRadiusMedium,
        boxShadow: 'none'
    },
});

// function to render dialog based on alert's alertDialog property
const renderDialog = (alert: TypeAlert) => {
    switch (alert.alertDialog) {
        case AlertDialogs.EffectivePrompts:
            return <EffectivePromptsDialog />;
        default:
            return null;
    }
}

export const Alerts: React.FC = () => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const { alerts } = useAppSelector((state: RootState) => state.app);

    return (
        <div>
            {alerts.map(({ type, message, hideDismissButton, showDialog }, index) => {
                return (
                    <Alert
                        intent={type}
                        action={hideDismissButton ? undefined : {
                            icon: (
                                <Dismiss16Regular
                                    aria-label="dismiss message"
                                    onClick={() => {
                                        dispatch(removeAlert(index));
                                    }}
                                    color="black"
                                />
                            ),
                        }}
                        key={`${index}-${type}`}
                        className={classes.alert}
                    >
                        {
                            showDialog ?
                                renderDialog(alerts[index]) :
                                message.slice(0, 1000) + (message.length > 1000 ? '...' : '')
                        }
                    </Alert>
                );
            })}
        </div>
    );
};
